import { useCallback, useEffect, useRef, useState } from 'react';
import Quill, { Delta } from 'quill';

import { sanitizeEmptyContent } from '@/shared/util';

import { FORMATS } from './editor.constant';
import { useQuillCharacterLimit } from './use-quill-character-limit.hook';
import {
  createQuillConfig,
  hasContentChanged,
  registerClipboardMatchers,
  updateQuillContentWithSelection,
} from './util';

interface UseBasicQuillEditorOptions {
  formats?: string[];
  isReadOnly?: boolean;
  maxLength?: number;
  onBlur?: (editedText: string, currentText?: string) => void;
  onChange?: (value: string) => void;
  placeholder?: string;
  value?: string;
}

export const useBasicQuillEditor = ({
  formats = FORMATS,
  isReadOnly = false,
  maxLength,
  onBlur,
  onChange,
  placeholder = '',
  value = '',
}: UseBasicQuillEditorOptions) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<null | Quill>(null);
  const sanitizedValue = sanitizeEmptyContent(value);
  const [initialValue, setInitialValue] = useState(sanitizedValue);
  const lastPropValueRef = useRef<string | undefined>(sanitizedValue);
  const isUserEditingRef = useRef(false);

  const {
    characterCount,
    enforceMaxLengthIfNeeded,
    getCurrentCharacterCount,
    isFocused,
    registerClipboardMatcher,
    setCharacterCount,
    setIsFocused,
    syncCharacterCount,
  } = useQuillCharacterLimit({ maxLength, quillRef });

  const handleTextChange = useCallback(
    (changeDelta: Delta) => {
      if (!quillRef.current) {
        return;
      }

      if (isReadOnly || !onChange) {
        return;
      }

      isUserEditingRef.current = true;

      if (typeof maxLength === 'number') {
        const nextCharacterCount = getCurrentCharacterCount();

        const didEnforceMaxLength = enforceMaxLengthIfNeeded(nextCharacterCount, changeDelta);

        if (!didEnforceMaxLength) {
          setCharacterCount(nextCharacterCount);
        }
      }

      const currentHtml = sanitizeEmptyContent(quillRef.current.root.innerHTML || '');

      lastPropValueRef.current = currentHtml;
      onChange(currentHtml);
    },
    [
      enforceMaxLengthIfNeeded,
      getCurrentCharacterCount,
      isReadOnly,
      maxLength,
      onChange,
      setCharacterCount,
    ],
  );

  const handleBlur = useCallback(
    (currentValue: string) => {
      if (!onBlur) {
        return;
      }

      const sanitizedCurrentValue = sanitizeEmptyContent(currentValue);
      const sanitizedPropValue = sanitizeEmptyContent(value);
      const editedText =
        initialValue !== sanitizedCurrentValue && sanitizedPropValue !== sanitizedCurrentValue
          ? sanitizedCurrentValue
          : '';

      onBlur(editedText, sanitizedCurrentValue);
    },
    [onBlur, initialValue, value],
  );

  const handleSelectionChange = useCallback(
    (range: unknown, oldRange: unknown) => {
      setIsFocused(range !== null);

      if (range !== null || oldRange === null || isReadOnly || !onBlur || !quillRef.current) {
        return;
      }

      const currentValue = sanitizeEmptyContent(quillRef.current.root.innerHTML || '');

      handleBlur(currentValue);
    },
    [handleBlur, isReadOnly, onBlur, setIsFocused],
  );

  const initializeQuill = useCallback(() => {
    if (!editorRef.current || quillRef.current) {
      return;
    }

    const config = createQuillConfig({
      formats,
      isReadOnly,
      placeholder,
    });

    quillRef.current = new Quill(editorRef.current, { ...config, bounds: editorRef.current });

    registerClipboardMatchers(quillRef.current);
    registerClipboardMatcher(quillRef.current);

    const html = quillRef.current.clipboard.convert({
      html: sanitizedValue,
    });

    quillRef.current.setContents(html);
    setInitialValue(quillRef.current.root.innerHTML || '');
    syncCharacterCount();
  }, [
    formats,
    isReadOnly,
    placeholder,
    registerClipboardMatcher,
    sanitizedValue,
    syncCharacterCount,
  ]);

  const updateQuillContent = useCallback(() => {
    if (!quillRef.current) {
      return;
    }

    const isExternalUpdate = sanitizedValue !== lastPropValueRef.current;

    if (isExternalUpdate) {
      lastPropValueRef.current = sanitizedValue;
      isUserEditingRef.current = false;
    }

    if (isUserEditingRef.current) {
      return;
    }

    if (!hasContentChanged(quillRef.current, sanitizedValue)) {
      return;
    }

    updateQuillContentWithSelection(quillRef.current, sanitizedValue);

    if (isExternalUpdate) {
      setInitialValue(quillRef.current.root.innerHTML || '');
      syncCharacterCount();
    }
  }, [sanitizedValue, syncCharacterCount]);

  useEffect(() => {
    initializeQuill();
  }, [initializeQuill]);

  useEffect(() => {
    quillRef.current?.enable(!isReadOnly);
  }, [isReadOnly]);

  useEffect(() => {
    updateQuillContent();
  }, [updateQuillContent]);

  useEffect(() => {
    if (!quillRef.current) {
      return undefined;
    }

    quillRef.current.on('text-change', handleTextChange);
    quillRef.current.on('selection-change', handleSelectionChange);

    return () => {
      quillRef.current?.off('text-change', handleTextChange);
      quillRef.current?.off('selection-change', handleSelectionChange);
    };
  }, [handleTextChange, handleSelectionChange]);

  const blur = useCallback(() => {
    quillRef.current?.blur();
  }, []);

  return {
    blur,
    characterCount,
    editorRef,
    isFocused,
  };
};
